import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const MetaSettings = () => {
  const [settings, setSettings] = useState({
    platformName: '',
    supportEmail: '',
    supportPhone: '',
    socialLinks: [],
    platformFeePercent: 10,
    maintenanceMode: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data && res.data.data) {
        setSettings(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const addSocialLink = () => {
    setSettings({
      ...settings,
      socialLinks: [...(settings.socialLinks || []), { platform: '', url: '', iconClass: 'fab fa-facebook' }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const newLinks = [...(settings.socialLinks || [])];
    newLinks[index][field] = value;
    setSettings({ ...settings, socialLinks: newLinks });
  };

  const removeSocialLink = (index) => {
    const newLinks = [...(settings.socialLinks || [])];
    newLinks.splice(index, 1);
    setSettings({ ...settings, socialLinks: newLinks });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put('/settings', settings);
      if (res.data.success) {
        alert('✅ Platform Settings saved successfully!');
      }
    } catch (err) {
      console.error(err);
      alert('❌ Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 flex justify-center text-stone-500">Loading settings...</div>;
  }

  return (
    <div className="p-8 pb-20">
      <div className="mb-8">
        <h2 className="font-serif text-3xl font-bold text-[#2A2858]">Platform Settings</h2>
        <p className="mt-1 text-stone-500 text-sm">Global configuration for the Ananda Marga platform</p>
      </div>

      <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#E8B84B]/20 max-w-4xl">
        <form onSubmit={handleSave} className="space-y-8">
          
          <section>
            <h3 className="font-bold text-stone-800 text-lg mb-4 border-b border-stone-100 pb-2">General Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Platform Name</label>
                <input 
                  type="text" 
                  name="platformName"
                  value={settings.platformName}
                  onChange={handleChange}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E8B84B] transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Platform Fee (%)</label>
                <input 
                  type="number" 
                  name="platformFeePercent"
                  value={settings.platformFeePercent}
                  onChange={handleChange}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E8B84B] transition-all"
                />
              </div>
            </div>
          </section>

          <section>
            <h3 className="font-bold text-stone-800 text-lg mb-4 border-b border-stone-100 pb-2">Contact Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Support Email</label>
                <input 
                  type="email" 
                  name="supportEmail"
                  value={settings.supportEmail}
                  onChange={handleChange}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E8B84B] transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Support Phone</label>
                <input 
                  type="text" 
                  name="supportPhone"
                  value={settings.supportPhone}
                  onChange={handleChange}
                  className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E8B84B] transition-all"
                />
              </div>
            </div>
          </section>

          {/* The donor project's "Frontend Hero" section — a homepage countdown
              and a VR hero upload — is gone with the VR wing itself. */}

          <section>
            <div className="flex items-center justify-between mb-4 border-b border-stone-100 pb-2">
              <h3 className="font-bold text-stone-800 text-lg">Social Links</h3>
              <button 
                type="button" 
                onClick={addSocialLink}
                className="text-xs bg-[#2A2858] text-white px-3 py-1 rounded-lg font-bold hover:bg-[#14122B] transition-colors"
              >
                + Add Link
              </button>
            </div>
            
            <div className="space-y-4">
              {(settings.socialLinks || []).map((link, index) => (
                <div key={index} className="flex flex-col md:flex-row gap-4 p-4 border border-stone-200 rounded-xl bg-stone-50">
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Platform Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Facebook"
                      value={link.platform}
                      onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#E8B84B]"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">URL</label>
                    <input 
                      type="url" 
                      placeholder="https://"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#E8B84B]"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">FA Icon Class</label>
                    <input 
                      type="text" 
                      placeholder="e.g. fab fa-facebook"
                      value={link.iconClass}
                      onChange={(e) => updateSocialLink(index, 'iconClass', e.target.value)}
                      className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#E8B84B]"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <button 
                      type="button" 
                      onClick={() => removeSocialLink(index)}
                      className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 p-2 rounded-lg transition-colors"
                      title="Remove Link"
                    >
                      <span className="material-symbols-outlined text-sm">delete</span>
                    </button>
                  </div>
                </div>
              ))}
              {(!settings.socialLinks || settings.socialLinks.length === 0) && (
                <div className="text-center text-sm text-stone-500 py-4">No social links added yet.</div>
              )}
            </div>
          </section>

          <section className="bg-stone-50 p-6 rounded-2xl border border-stone-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-stone-800">Maintenance Mode</h3>
                <p className="text-stone-500 text-xs mt-1">Enable this to temporarily disable the frontend application for updates.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  name="maintenanceMode"
                  checked={settings.maintenanceMode} 
                  onChange={handleChange} 
                  className="sr-only peer" 
                />
                <div className="w-11 h-6 bg-stone-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2A2858]"></div>
              </label>
            </div>
          </section>

          <div className="pt-4 border-t border-stone-100 flex justify-end">
            <button 
              type="submit"
              disabled={saving}
              className="bg-[#2A2858] text-white px-8 py-3 rounded-xl font-bold text-sm shadow-lg hover:bg-[#14122B] transition-colors flex items-center gap-2"
            >
              {saving ? 'Saving...' : <><span className="material-symbols-outlined text-sm font-bold">save</span> Save Settings</>}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default MetaSettings;
